import {
  Form,
  FormField,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  DateTimePicker,
} from "@/components/ui";
import { useAppDispatch, useAppSelector } from "@/config/store";
import {
  IEdoCourseQuiz,
  defaultValue,
} from "@/shared/model/edo-course-quiz.model";
import {
  createEntity,
  updateEntity,
} from "@/shared/reducers/entities/edo-course-quiz.reducer";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Translate, translate } from "react-jhipster";
import { z } from "zod";

const schema = z.object({
  availableAttempts: z.union([z.string(), z.number()]).optional(),
  duration: z.union([z.string(), z.number()]).optional(),
  timeOpen: z.string().nullable().optional(),
  timeClose: z.string().nullable().optional(),
});

type FormValues = z.infer<typeof schema>;

interface IEditCourseQuizProps {
  courseQuiz?: IEdoCourseQuiz;
  open: boolean;
  onClose: () => void;
}

const EditCourseQuizFormContent = () => {
  const { control } = useFormContext<FormValues>();

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div className="col-span-full sm:col-span-1">
        <FormField
          name="availableAttempts"
          label={translate("edocendoApp.edoCourseQuiz.availableAttempts")}
          placeholder={translate("edocendoApp.edoCourseQuiz.availableAttempts")}
        />
      </div>
      <div className="col-span-full sm:col-span-1">
        <FormField
          name="duration"
          label={
            translate("edocendoApp.edoCourseQuiz.duration") + " (min)"
          }
          placeholder={translate("edocendoApp.edoCourseQuiz.duration")}
        />
      </div>
      <div className="col-span-full sm:col-span-1">
        <Controller
          name="timeOpen"
          control={control}
          render={({ field }) => (
            <DateTimePicker
              label={translate("edocendoApp.edoCourseQuiz.timeOpen")}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </div>
      <div className="col-span-full sm:col-span-1">
        <Controller
          name="timeClose"
          control={control}
          render={({ field }) => (
            <DateTimePicker
              label={translate("edocendoApp.edoCourseQuiz.timeClose")}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </div>
    </div>
  );
};

const EditCourseQuiz = (props: IEditCourseQuizProps) => {
  const dispatch = useAppDispatch();
  const { loading, updating, updateSuccess } = useAppSelector<
    EntityState<IEdoCourseQuiz>
  >((state) => state.edoCourseQuiz);

  const [form, setForm] = useState<IEdoCourseQuiz>({
    ...defaultValue,
    ...props.courseQuiz,
  });

  const { courseQuiz, open, onClose: handleClose } = props;
  const isNew = courseQuiz === null || courseQuiz === undefined;

  useEffect(() => {
    if (courseQuiz) {
      setForm({ ...defaultValue, ...courseQuiz });
    } else {
      setForm({ ...defaultValue });
    }
  }, [courseQuiz]);

  useEffect(() => {
    if (open && updateSuccess) handleClose();
  }, [updateSuccess, open]);

  const saveEntity = (data: FormValues) => {
    const entity = {
      ...form,
      availableAttempts:
        data.availableAttempts != null
          ? Number(data.availableAttempts)
          : form.availableAttempts,
      duration:
        data.duration != null ? Number(data.duration) : form.duration,
      timeOpen: data.timeOpen ?? form.timeOpen,
      timeClose: data.timeClose ?? form.timeClose,
    };
    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {translate("edocendoApp.edoCourseQuiz.home.createOrEditLabel")}
          </DialogTitle>
        </DialogHeader>
        {loading && !courseQuiz ? (
          <div className="flex justify-center py-4">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : (
          <Form<FormValues>
            id="edit-course-quiz-form"
            key={`${form.id ?? "new"}`}
            schema={schema}
            onSubmit={saveEntity}
            defaultValues={{
              availableAttempts: form.availableAttempts ?? "",
              duration: form.duration ?? "",
              timeOpen: form.timeOpen ?? null,
              timeClose: form.timeClose ?? null,
            }}
            className="flex flex-col gap-4"
          >
            <EditCourseQuizFormContent />
          </Form>
        )}
        <DialogFooter>
          <Button variant="outline-solid" onClick={handleClose}>
            <Translate contentKey="entity.action.cancel">Cancel</Translate>
          </Button>
          <Button
            type="submit"
            form="edit-course-quiz-form"
            disabled={updating}
          >
            <Translate contentKey="entity.action.edit">Modifica</Translate>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditCourseQuiz;
